import React from "react"
import clsx from "clsx"
import { CommentAttachedMedia } from "../../requests/comments"
import { SpinnerSmall } from "../shared/Spinner"

interface Props {
  media: CommentAttachedMedia[]
  altText: string
}

// poster_url comes from Mux itself, available as soon as the asset's
// playback_id is set - well before the video is actually transcoded/
// playable (see MuxAsset#poster_url) - so this often has a real frame from
// the video, not just a generic placeholder, even while still "processing".
const ProcessingVideo: React.FC<{ posterUrl?: string | null }> = ({ posterUrl }) => {
  return (
    <div
      // Both children below are position: absolute once a poster is
      // present, so nothing is left in normal flow to give this box a
      // height - it needs an explicit height (not just maxHeight) or it
      // collapses to 0px and overflow-hidden clips it to invisible.
      style={{ height: "200px", width: "150px", position: "relative" }}
      className="flex items-center justify-center overflow-hidden bg-gray-100"
    >
      {posterUrl && (
        <img src={posterUrl} alt="" className="absolute inset-0 h-full w-full object-cover" />
      )}
      <div
        className={clsx(
          "flex flex-col items-center justify-center gap-1 p-4",
          posterUrl && "absolute inset-0",
        )}
        // bg-opacity-* isn't available in this project's Tailwind version
        // (1.2.0, predates the backgroundOpacity core plugin) - bg-black
        // alone would render fully opaque and hide the thumbnail entirely.
        style={posterUrl ? { backgroundColor: "rgba(0, 0, 0, 0.4)" } : undefined}
      >
        <SpinnerSmall className={posterUrl ? "text-white" : "text-gray-500"} />
        <span className={clsx("text-xs", posterUrl ? "text-white" : "text-gray-500")}>
          Processing video…
        </span>
      </div>
    </div>
  )
}

// Videos are transcoded by Mux asynchronously (VideoConverterService kicks
// off the Mux asset, the Mux webhook flips video_ready once it's done).
// Until then, show a "processing" placeholder instead of a broken/unplayable
// <video> tag.
const CommentMedia: React.FC<Props> = ({ media, altText }) => {
  const playable = media.filter(
    (item) => item.content_type_generic === "image" || item.video_ready,
  )
  const processing = media.filter(
    (item) => item.content_type_generic === "video" && !item.video_ready,
  )
  const items = [...playable, ...processing]

  return (
    <div className="mt-1 flex flex-wrap gap-1">
      {items.map((item) =>
        item.content_type_generic === "video" ? (
          item.video_ready ? (
            <video
              key={item.signed_id}
              style={{ maxHeight: "250px" }}
              className="object-contain"
              poster={item.poster_url ?? undefined}
              preload="metadata"
              controls
            >
              <source src={item.url ?? undefined} type="video/mp4" />
            </video>
          ) : (
            <ProcessingVideo key={item.signed_id} posterUrl={item.poster_url} />
          )
        ) : (
          <a
            key={item.signed_id}
            href={item.url ?? undefined}
            target="_blank"
            rel="noreferrer"
          >
            <img
              style={{ maxHeight: "250px" }}
              src={item.url ?? undefined}
              alt={altText}
              className="object-contain"
            />
          </a>
        ),
      )}
    </div>
  )
}

export default CommentMedia
